import { useAuthProfile } from '@/hooks/queries/useAuthProfile';
import { authQueryKeys } from '@/hooks/queries/auth.queries';
import { AuthService } from '@/services/auth.service';
import { unsubscribeFromPushNotifications } from '@/lib/push-notifications';
import type { SignInUserDto } from '@/types/dto/auth.dto';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState, type PropsWithChildren } from 'react';
import { AuthContext, type AuthContextValue } from './auth-context';

export function AuthProvider({ children }: PropsWithChildren) {
  const queryClient = useQueryClient();
  const { profile, isError, isLoading } = useAuthProfile();
  const [verificationToken, setVerificationTokenState] = useState<string | null>(null);
  const user = profile ?? null;

  const refreshAuthState = useCallback(async () => {
    const profileQueryKey = authQueryKeys.profile();

    await queryClient.invalidateQueries({ queryKey: profileQueryKey });
    await queryClient.refetchQueries({ queryKey: profileQueryKey, type: 'active' });
  }, [queryClient]);

  const clearAuth = useCallback(() => {
    setVerificationTokenState(null);
    queryClient.setQueryData(authQueryKeys.profile(), null);
  }, [queryClient]);

  const signIn = useCallback(
    async (data: SignInUserDto) => {
      const response = await AuthService.signIn(data);

      queryClient.setQueryData(authQueryKeys.profile(), response.user);
      await refreshAuthState();

      return response;
    },
    [queryClient, refreshAuthState],
  );

  const logout = useCallback(async () => {
    await unsubscribeFromPushNotifications().catch(() => undefined);
    await AuthService.logout();
    clearAuth();
  }, [clearAuth]);

  const value = useMemo<AuthContextValue>(
    () => ({
      clearAuth,
      isAuthenticated: Boolean(user) && !isError,
      isLoading,
      logout,
      setVerificationToken: setVerificationTokenState,
      signIn,
      user,
      verificationToken,
    }),
    [clearAuth, isError, isLoading, logout, signIn, user, verificationToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
